import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Zap } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const LANG_BAR_H = 34;

const Navbar = () => {
  const t = useTr();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t('Architecture', '아키텍처'), to: '/architecture' },
    { label: t('Tokenomics', '토크노믹스'),   to: '/tokenomics' },
    { label: t('Roadmap', '로드맵'),      to: '/roadmap' },
    { label: t('Ecosystem', '생태계'),    to: '/ecosystem' },
    { label: t('Team', '팀'),         to: '/team' },
    { label: t('Whitepaper', '백서'),   to: '/whitepaper' },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <>
      {/* Scroll progress bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, zIndex: 200, background: 'rgba(255,255,255,0.05)' }}>
        <div style={{ height: '100%', width: `${scrollProgress}%`, background: 'linear-gradient(90deg, #55d532, #30d5c8)', transition: 'width 0.1s linear', boxShadow: '0 0 8px rgba(48,213,200,0.7)' }} />
      </div>

      <nav
        data-testid="navbar"
        className="fixed left-0 right-0 z-[100] transition-all duration-500"
        style={{
          top: LANG_BAR_H,
          background: scrolled ? 'rgba(0,0,0,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
          boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.5)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 py-3.5 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img
              src="/logo.svg"
              alt="SwarmBase logo"
              className="w-8 h-8 sm:w-9 sm:h-9 object-contain"
            />
            <span
              className="font-outfit font-black tracking-tight text-base sm:text-lg leading-none"
              style={{ color: '#ffffff', textShadow: '0 1px 6px rgba(0,0,0,0.95), 0 0 16px rgba(0,0,0,0.8)' }}
            >
              Swarm<span style={{ color: '#30d5c8' }}>Base</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="highlight-underline text-sm font-medium tracking-wide transition-colors"
                style={{
                  color: isActive(link.to) ? '#30d5c8' : 'rgba(255,255,255,0.75)',
                  textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                  fontWeight: isActive(link.to) ? '700' : '500',
                }}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center">
            <a
              href="https://core.swarmbase.io"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-btn inline-flex items-center gap-1.5 px-5 py-2 rounded text-sm font-semibold transition-all duration-300"
              style={{ border: '1px solid #30d5c8', color: '#30d5c8', background: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#30d5c8'; e.currentTarget.style.color = '#000000'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#30d5c8'; }}
              data-testid="nav-open-dapp"
            >
              <Zap size={12} />
              {t('Open dApp', 'dApp 열기')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-1 transition-colors"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden px-5 py-5 flex flex-col gap-4"
            style={{ background: 'rgba(0,0,0,0.98)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}
                className="text-sm font-medium py-1 transition-colors"
                style={{ color: isActive(link.to) ? '#30d5c8' : 'rgba(255,255,255,0.7)' }}
                onClick={() => setMenuOpen(false)}
                data-testid={`nav-mobile-${link.label.toLowerCase()}`}>
                {link.label}
              </Link>
            ))}
            <div className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <a href="https://core.swarmbase.io" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded text-sm font-semibold swarm-glow"
                style={{ border: '1px solid #30d5c8', color: '#30d5c8' }}
                data-testid="nav-mobile-dapp">
                <Zap size={12} />{t('Open dApp', 'dApp 열기')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
